import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CalendarDays, LogIn, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function PickupDrop() {
  const [dateStr, setDateStr] = useState(new Date().toISOString().slice(0, 10));
  const [children, setChildren] = useState([]);
  const [logs, setLogs] = useState({});
  const [drafts, setDrafts] = useState({}); // per-child input values

  const load = async () => {
    const [c, l] = await Promise.all([
      api.get("/children"),
      api.get("/pickup-drop", { params: { date: dateStr } }),
    ]);
    setChildren(c.data);
    const map = {};
    l.data.forEach((r) => { map[r.child_id] = r; });
    setLogs(map);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateStr]);

  const setDraft = (cid, field, value) => {
    setDrafts({ ...drafts, [cid]: { ...(drafts[cid] || {}), [field]: value } });
  };

  const logDrop = async (c) => {
    const d = drafts[c.id] || {};
    if (!d.drop_name) { toast.error("Enter who dropped"); return; }
    try {
      const res = await api.post("/pickup-drop", {
        child_id: c.id, date: dateStr,
        dropped_by_name: d.drop_name, dropped_by_relation: d.drop_relation || "",
      });
      setLogs({ ...logs, [c.id]: res.data });
      setDrafts({ ...drafts, [c.id]: { ...d, drop_name: "", drop_relation: "" } });
      toast.success(`${c.name} · Drop logged`);
    } catch { toast.error("Failed"); }
  };

  const logPickup = async (c) => {
    const d = drafts[c.id] || {};
    if (!d.pickup_name) { toast.error("Enter who picked up"); return; }
    try {
      const res = await api.post("/pickup-drop", {
        child_id: c.id, date: dateStr,
        picked_by_name: d.pickup_name, picked_by_relation: d.pickup_relation || "",
      });
      setLogs({ ...logs, [c.id]: res.data });
      setDrafts({ ...drafts, [c.id]: { ...d, pickup_name: "", pickup_relation: "" } });
      toast.success(`${c.name} · Pickup logged`);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black">Pickup / Drop Log</h1>
          <p className="text-muted-foreground font-medium">Quick check-in and check-out. Timestamps captured automatically.</p>
        </div>
        <div className="flex items-center gap-2 card-soft rounded-full px-4 py-2">
          <CalendarDays size={18} className="text-primary" />
          <Input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} className="border-0 h-9 p-0 focus-visible:ring-0 font-semibold w-36" />
        </div>
      </header>

      <div className="space-y-3">
        {children.map((c) => {
          const log = logs[c.id];
          const d = drafts[c.id] || {};
          return (
            <div key={c.id} className="card-soft rounded-2xl p-4 space-y-3" data-testid={`pd-${c.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl chip-amber flex items-center justify-center font-black">
                  {c.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </div>
                <div className="flex-1">
                  <div className="font-extrabold">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.class_name}</div>
                </div>
                {log?.drop_time && <span className="chip-green px-3 py-1 rounded-full text-xs font-bold">🟢 In: {log.dropped_by_name}</span>}
                {log?.pickup_time && <span className="chip-teal px-3 py-1 rounded-full text-xs font-bold">🔵 Out: {log.picked_by_name}</span>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {!log?.drop_time ? (
                  <div className="rounded-2xl bg-muted/40 p-3 space-y-2">
                    <div className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-1"><LogIn size={12} /> Drop-off</div>
                    <div className="flex gap-2">
                      <Input placeholder="Dropped by (name)" value={d.drop_name || ""} onChange={(e) => setDraft(c.id, "drop_name", e.target.value)} className="h-10 rounded-xl" />
                      <Input placeholder="Relation" value={d.drop_relation || ""} onChange={(e) => setDraft(c.id, "drop_relation", e.target.value)} className="h-10 rounded-xl w-32" />
                    </div>
                    <Button size="sm" data-testid={`log-drop-${c.id}`} onClick={() => logDrop(c)} className="rounded-full w-full">Log Drop</Button>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-primary/5 p-3">
                    <div className="text-xs font-bold uppercase text-muted-foreground">Drop-off logged</div>
                    <div className="font-bold">{log.dropped_by_name} <span className="font-normal text-muted-foreground">({log.dropped_by_relation || "—"})</span></div>
                    <div className="text-xs text-muted-foreground">{new Date(log.drop_time).toLocaleTimeString()}</div>
                  </div>
                )}

                {!log?.pickup_time ? (
                  <div className="rounded-2xl bg-muted/40 p-3 space-y-2">
                    <div className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-1"><LogOut size={12} /> Pickup</div>
                    <div className="flex gap-2">
                      <Input placeholder="Picked up by (name)" value={d.pickup_name || ""} onChange={(e) => setDraft(c.id, "pickup_name", e.target.value)} className="h-10 rounded-xl" />
                      <Input placeholder="Relation" value={d.pickup_relation || ""} onChange={(e) => setDraft(c.id, "pickup_relation", e.target.value)} className="h-10 rounded-xl w-32" />
                    </div>
                    <Button size="sm" data-testid={`log-pickup-${c.id}`} onClick={() => logPickup(c)} className="rounded-full w-full">Log Pickup</Button>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-primary/5 p-3">
                    <div className="text-xs font-bold uppercase text-muted-foreground">Pickup logged</div>
                    <div className="font-bold">{log.picked_by_name} <span className="font-normal text-muted-foreground">({log.picked_by_relation || "—"})</span></div>
                    <div className="text-xs text-muted-foreground">{new Date(log.pickup_time).toLocaleTimeString()}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {children.length === 0 && <div className="text-center py-12 text-muted-foreground">No children.</div>}
      </div>
    </div>
  );
}
